import type { Database } from "@base-hormonal/database";
import type { StorageService } from "../../../shared/api/storage";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";
import { SKINFOLD_FIELDS } from "../model/skinfolds";

type AssessmentRow = Database["public"]["Tables"]["avaliacoes_corporais"]["Row"];
export type PhotoRow = Database["public"]["Tables"]["avaliacao_fotos"]["Row"];

export interface Assessment extends AssessmentRow {
  avaliacao_fotos: PhotoRow[];
}

export const ASSESSMENT_PAGE_SIZE = 20;

const ASSESSMENT_COLUMNS =
  "id,aluno_id,user_id,data,peso_kg,altura_cm,percentual_gordura,massa_magra_kg,medidas,composicao,protocolo_avaliacao,observacoes,created_at,updated_at," +
  "avaliacao_fotos!avaliacao_fotos_avaliacao_aluno_fk(id,avaliacao_id,aluno_id,posicao,storage_path,autorizada_relatorio,observacao,created_at)";

export interface AssessmentInput {
  id?: string | null;
  aluno_id: string;
  data: string;
  peso_kg: number;
  altura_cm: number | null;
  percentual_gordura: number | null;
  massa_magra_kg: number | null;
  medidas: Record<string, number>;
  protocolo_avaliacao: string | null;
  observacoes: string | null;
}

export interface PhotoUploadInput {
  aluno_id: string;
  avaliacao_id: string;
  file: File;
  posicao: string;
  /** Opt-in explícito: a foto só entra em relatório quando autorizada aqui. */
  autorizada_relatorio: boolean;
  maxBytes?: number | null;
}

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

/**
 * Escritor único de avaliações e fotos privadas. A foto só existe
 * quando o objeto e o metadado existem juntos: o upload é compensado se a
 * linha falhar, e a linha é apagada antes do objeto quando o usuário remove.
 */
export function createBodyApi(client: TypedSupabaseClient, storage: StorageService) {
  return {
    async assessments(profileId: string, page = 0, signal?: AbortSignal): Promise<Assessment[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("avaliacoes_corporais")
          .select(ASSESSMENT_COLUMNS)
          .eq("aluno_id", profileId)
          .order("data", { ascending: false })
          .range(page * ASSESSMENT_PAGE_SIZE, (page + 1) * ASSESSMENT_PAGE_SIZE - 1)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as Assessment[];
      });
    },

    /** URLs temporárias das fotos, em um lote por conjunto de paths. */
    async photoUrls(
      paths: readonly string[],
      seconds?: number | null,
      signal?: AbortSignal,
    ): Promise<Map<string, string>> {
      return storage.signedUrls("body-progress", paths, seconds, signal);
    },

    async saveAssessment(userId: string, input: AssessmentInput): Promise<string> {
      if (input.data === "" || !(input.peso_kg > 0)) {
        throw new PublicAppError("invalid_input", "Informe uma data e um peso válido.");
      }
      for (const [, key] of SKINFOLD_FIELDS) {
        const value = input.medidas[key];
        if (value !== undefined && (!Number.isFinite(value) || value <= 0 || value > 200)) {
          throw new PublicAppError(
            "invalid_input",
            "Dobras cutâneas devem estar entre 0 e 200 mm.",
          );
        }
      }
      const values = {
        aluno_id: input.aluno_id,
        user_id: userId,
        data: input.data,
        peso_kg: input.peso_kg,
        altura_cm: input.altura_cm,
        percentual_gordura: input.percentual_gordura,
        massa_magra_kg: input.massa_magra_kg,
        medidas: input.medidas,
        composicao: {},
        protocolo_avaliacao: input.protocolo_avaliacao,
        observacoes: input.observacoes,
      };
      return withRequestSignal(undefined, async (signal) => {
        const result =
          input.id != null && input.id !== ""
            ? await client
                .from("avaliacoes_corporais")
                .update(values)
                .eq("id", input.id)
                .select("id")
                .abortSignal(signal)
                .single()
            : await client
                .from("avaliacoes_corporais")
                .insert(values)
                .select("id")
                .abortSignal(signal)
                .single();
        raise(result.error);
        if (result.data === null) {
          throw new PublicAppError("empty_response", "A avaliação não foi salva.");
        }
        return result.data.id;
      });
    },

    async uploadPhoto(userId: string, input: PhotoUploadInput): Promise<string> {
      return storage.uploadThenCommit(
        {
          bucket: "body-progress",
          segments: [userId, input.aluno_id, input.avaliacao_id],
          file: input.file,
          maxBytes: input.maxBytes ?? null,
        },
        "corpo",
        async (uploaded) =>
          withRequestSignal(undefined, async (signal) => {
            const { data, error } = await client
              .from("avaliacao_fotos")
              .insert({
                avaliacao_id: input.avaliacao_id,
                aluno_id: input.aluno_id,
                user_id: userId,
                posicao: input.posicao,
                storage_path: uploaded.path,
                autorizada_relatorio: input.autorizada_relatorio,
              })
              .select("id")
              .abortSignal(signal)
              .single();
            raise(error);
            if (data === null) {
              throw new PublicAppError("empty_response", "A foto não foi registrada.");
            }
            return data.id;
          }),
      );
    },

    async setPhotoAuthorization(photoId: string, authorized: boolean): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("avaliacao_fotos")
          .update({ autorizada_relatorio: authorized })
          .eq("id", photoId)
          .abortSignal(signal);
        raise(error);
      });
    },

    async deletePhoto(photo: Pick<PhotoRow, "id" | "storage_path">): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("avaliacao_fotos")
          .delete()
          .eq("id", photo.id)
          .abortSignal(signal);
        raise(error);
      });
      await storage.removeQuietly("body-progress", [photo.storage_path], "corpo");
    },

    async deleteAssessment(assessment: Assessment): Promise<void> {
      const paths = assessment.avaliacao_fotos.map((photo) => photo.storage_path);
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("avaliacoes_corporais")
          .delete()
          .eq("id", assessment.id)
          .abortSignal(signal);
        raise(error);
      });
      if (paths.length > 0) await storage.removeQuietly("body-progress", paths, "corpo");
    },

  };
}

export type BodyApi = ReturnType<typeof createBodyApi>;
